import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Tag, X, Plus } from "@phosphor-icons/react";

/**
 * Rich conversation tags editor — Sprint 31C · Feb 2026.
 * Renders current tags as chips (removable) + a small dropdown with the
 * catalog to add new ones. Auto-persists via PATCH /conversations/{cid}/tags.
 */
const TAG_COLORS = {
  "VIP":              "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/40",
  "Lead Nuevo":       "bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-500/40",
  "Urgente":          "bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-500/40",
  "Pendiente Docs":   "bg-orange-500/15 text-orange-700 dark:text-orange-300 border-orange-500/40",
  "En Proceso":       "bg-sky-500/15 text-sky-700 dark:text-sky-300 border-sky-500/40",
  "Cotización":       "bg-purple-500/15 text-purple-700 dark:text-purple-300 border-purple-500/40",
  "Test Drive":       "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/40",
  "Servicio":         "bg-cyan-500/15 text-cyan-700 dark:text-cyan-300 border-cyan-500/40",
  "Repuestos":        "bg-teal-500/15 text-teal-700 dark:text-teal-300 border-teal-500/40",
  "Garantía":         "bg-indigo-500/15 text-indigo-700 dark:text-indigo-300 border-indigo-500/40",
  "Financiamiento":   "bg-lime-500/15 text-lime-700 dark:text-lime-300 border-lime-500/40",
  "Recompra":         "bg-fuchsia-500/15 text-fuchsia-700 dark:text-fuchsia-300 border-fuchsia-500/40",
  "Empresa":          "bg-slate-500/15 text-slate-700 dark:text-slate-300 border-slate-500/40",
  "Sin Respuesta":    "bg-gray-500/15 text-gray-700 dark:text-gray-300 border-gray-500/40",
};

const defaultColor = "bg-primary/10 text-primary border-primary/40";

export default function ConversationTagsEditor({ conversationId, initialTags = [] }) {
  const [tags, setTags] = useState(initialTags);
  const [catalog, setCatalog] = useState({ default: [], custom: [] });
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newTag, setNewTag] = useState("");

  useEffect(() => { setTags(initialTags || []); }, [initialTags, conversationId]);

  useEffect(() => {
    api.get("/conversations/tags/catalog")
      .then((r) => setCatalog(r.data))
      .catch(() => {});
  }, []);

  const persist = async (next) => {
    setSaving(true);
    try {
      await api.patch(`/conversations/${conversationId}/tags`, { tags: next });
      setTags(next);
    } catch {
      toast.error("Error al actualizar etiquetas");
    } finally { setSaving(false); }
  };

  const addTag = (t) => {
    const clean = String(t).trim();
    if (!clean || tags.includes(clean) || tags.length >= 12) return;
    persist([...tags, clean]);
    setNewTag("");
  };

  const removeTag = (t) => persist(tags.filter((x) => x !== t));

  const suggestions = [...(catalog.default || []), ...(catalog.custom || [])]
    .filter((t) => !tags.includes(t));

  return (
    <div className="flex flex-wrap items-center gap-1" data-testid="conversation-tags-editor">
      {tags.map((t) => (
        <span
          key={t}
          data-testid={`tag-chip-${t}`}
          className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold border ${TAG_COLORS[t] || defaultColor}`}
        >
          <Tag size={9} weight="fill" /> {t}
          <button
            onClick={() => removeTag(t)}
            data-testid={`tag-remove-${t}`}
            className="opacity-60 hover:opacity-100 ml-0.5"
            aria-label={`Quitar ${t}`}
          >
            <X size={9} weight="bold" />
          </button>
        </span>
      ))}
      <div className="relative">
        <button
          onClick={() => setOpen((v) => !v)}
          disabled={saving || tags.length >= 12}
          data-testid="tag-add-btn"
          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold border border-dashed border-muted-foreground/40 text-muted-foreground hover:bg-muted"
        >
          <Plus size={9} weight="bold" /> Etiqueta
        </button>
        {open && (
          <div className="absolute z-30 top-full mt-1 right-0 bg-popover border border-border rounded-md shadow-lg p-2 w-56 max-h-72 overflow-auto" data-testid="tag-add-menu">
            <input
              value={newTag}
              onChange={(e) => setNewTag(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { addTag(newTag); setOpen(false); } }}
              placeholder="Nueva etiqueta…"
              data-testid="tag-new-input"
              className="w-full h-7 px-2 rounded border border-input bg-background text-[11px] mb-2"
            />
            <div className="flex flex-wrap gap-1">
              {suggestions.map((t) => (
                <button
                  key={t}
                  onClick={() => { addTag(t); setOpen(false); }}
                  data-testid={`tag-suggest-${t}`}
                  className={`text-[10px] px-1.5 py-0.5 rounded-full border ${TAG_COLORS[t] || defaultColor} hover:opacity-80`}
                >
                  {t}
                </button>
              ))}
              {suggestions.length === 0 && (
                <div className="text-[10px] text-muted-foreground py-2 text-center w-full">
                  Sin más sugerencias — escribe una nueva arriba.
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
